/**
 * Top-level mount point for an authenticated SPA session.
 *
 * The Amplify `<Authenticator>` UI was removed in the simpl_auth
 * migration: simpl-api no longer ships its own login form. Instead,
 * `<SimplAuthGuard>` probes the session via `/api/user` and either
 *
 *   - renders `<DefaultHome>` if the caller has a valid Cognito or
 *     simpl_auth JWT (the bridge converts the former into the latter
 *     transparently — see `cognitoExchangeBridge.ts`), or
 *   - bounces the browser to `simplAuthSignInUrl()` (the default
 *     `unauthedFallback` is `<SignInRedirect>`), at which point
 *     simpl_auth runs the full sign-in / MFA-enrollment / MFA-challenge
 *     flow and redirects back here with a `SIMPL_AUTH_JWT_<env>`
 *     cookie set on `.simpl.org`.
 *
 * `ENABLE_AUTH = false` is the only escape hatch that still makes
 * sense — local UI work where wiring up a real session is not worth
 * the cost.
 */

import { DefaultHome } from "./DefaultHome";
import { SimplAuthGuard } from "./features/Auth/SimplAuthGuard";
import { DevContextBanner } from "./features/DevContext/DevContextBanner";

const ENABLE_AUTH = true;

export const AuthenticatedApp = () => {
  // Layout order (top to bottom):
  //   1. <DevContextBanner> — worktree + branch strip, only shows in
  //      local dev; self-hides in prod (see the component).
  //   2. <SimplAuthGuard> — owns its own <simpl-impersonation-banner>
  //      mount above every surface it renders (SE-787), so the
  //      impersonation strip appears just below the dev-context strip
  //      and stays visible on error surfaces too.
  //   3. <DefaultHome> — the actual app.
  return (
    <>
      <DevContextBanner />
      <SimplAuthGuard>
        <DefaultHome />
      </SimplAuthGuard>
    </>
  );
};

export const NonAuthenticatedApp = () => <DefaultHome />;

export default ENABLE_AUTH ? AuthenticatedApp : NonAuthenticatedApp;
